import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { api, refreshSession, SESSION_EXPIRED_EVENT } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ACTIVITY_KEY, LOGOUT_KEY, EXPIRED_FLAG, markActivity, lastActivity } from "@/lib/activity";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
} from "@/components/ui/alert-dialog";

const EVENTS = ["mousemove", "click", "keydown", "scroll", "touchstart"];
const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export function SessionTimeout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState({ idle: 10, warn: 2 });
  const [left, setLeft] = useState(null);
  const warning = useRef(false);
  const ending = useRef(false);
  const lastWrite = useRef(0);

  useEffect(() => {
    if (!user) return;
    api.get("/settings/session").then((r) => setCfg({ idle: r.data.idle_minutes, warn: r.data.warning_minutes })).catch(() => {});
  }, [user]);

  const expire = useCallback(async () => {
    if (ending.current) return;
    ending.current = true;
    warning.current = false;
    setLeft(null);
    try { localStorage.setItem(LOGOUT_KEY, String(Date.now())); sessionStorage.setItem(EXPIRED_FLAG, "1"); } catch {}
    await logout();
    navigate("/login?expired=1", { replace: true });
  }, [logout, navigate]);

  useEffect(() => {
    if (!user) { ending.current = false; return; }
    if (!lastActivity()) markActivity();
    const idleMs = cfg.idle * 60000;
    const warnMs = cfg.warn * 60000;
    const onActivity = () => {
      if (warning.current) return;
      const now = Date.now();
      if (now - lastWrite.current > 1000) { lastWrite.current = now; markActivity(); }
    };
    const tick = () => {
      const idle = Date.now() - lastActivity();
      if (idle >= idleMs) { expire(); return; }
      if (idle >= idleMs - warnMs) { warning.current = true; setLeft(Math.ceil((idleMs - idle) / 1000)); }
      else if (warning.current) { warning.current = false; setLeft(null); }
    };
    const onStorage = (e) => {
      if (e.key === LOGOUT_KEY && e.newValue) { ending.current = false; expire(); }
      if (e.key === ACTIVITY_KEY) tick();
    };
    tick();
    const t = setInterval(tick, 1000);
    const onExpired = () => { ending.current = false; expire(); };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    const keep = setInterval(() => { if (Date.now() - lastActivity() < idleMs - warnMs) refreshSession().catch(() => {}); }, 10 * 60000);
    EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(t);
      clearInterval(keep);
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
      EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [user, cfg, expire]);

  const keepAlive = async () => {
    warning.current = false; setLeft(null); markActivity();
    try { await refreshSession(); } catch { expire(); }
  };

  return (
    <AlertDialog open={left !== null}>
      <AlertDialogContent className="max-w-md" data-testid="session-timeout-modal">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />Tu sesión está a punto de expirar
          </AlertDialogTitle>
          <AlertDialogDescription>Hemos detectado que no has tenido actividad recientemente.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="text-center space-y-2 py-2">
          <p className="text-sm text-muted-foreground">Tu sesión se cerrará automáticamente en:</p>
          <p className="text-4xl font-extrabold tabular-nums text-primary" data-testid="session-timeout-countdown">
            {fmt(left || 0)} <span className="text-base font-medium text-muted-foreground">min</span>
          </p>
          <p className="text-sm">¿Deseas mantener tu sesión activa?</p>
        </div>
        <AlertDialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={expire} data-testid="session-timeout-logout">Cerrar Sesión</Button>
          <Button onClick={keepAlive} data-testid="session-timeout-keep">Mantener Sesión Activa</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
